/**
 * Export endpoints: walk lists and route CSVs.
 *
 * Both endpoints return file content (CSV or HTML). The fetchers return
 * Blobs so the caller can trigger a browser download or open in a new tab.
 */
import client from '@/api/client';

/**
 * Download a CSV export of a route's voters, sorted by sequence.
 * Columns: Sequence, Voter Name, Address, Precinct, Tier, Score
 *
 * GET /routes/{route_id}/export/csv
 */
export async function downloadRouteCsv(routeId: number): Promise<Blob> {
  const response = await client.get(`/routes/${routeId}/export/csv`, {
    responseType: 'blob',
  });
  return response.data;
}

/**
 * Download a walk list for a precinct, optionally filtered by tier.
 *
 * GET /voters/walk-list/export?precinct_num=X&tier=Y&format=html|csv
 *
 * When format=html, returns a print-ready HTML page (content-type: text/html).
 * When format=csv, returns CSV with columns: First Name, Last Name, Address, Precinct, Tier, Score
 */
export async function downloadWalkList(
  precinctNum: number | string,
  opts?: { tier?: string; format?: 'html' | 'csv' }
): Promise<Blob> {
  const params: Record<string, string> = {
    precinct_num: String(precinctNum),
    format: opts?.format ?? 'csv',
  };
  if (opts?.tier) {
    params.tier = opts.tier;
  }

  const response = await client.get('/voters/walk-list/export', {
    params,
    responseType: 'blob',
  });
  return response.data;
}

/**
 * Helper: trigger a browser download by creating a temporary <a> element.
 */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Helper: open an HTML blob in a new tab for print preview.
 */
export function openHtmlInNewTab(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  // Note: do NOT revoke the URL immediately; the new tab needs time to consume it.
  // The browser will clean up the blob reference when the tab closes.
}
